/* Pure isometric-cube math, extracted from Scene.tsx and BrandMark.tsx
   without changing either file. No React, no DOM — this is just the
   projection: given a front-bottom corner and an edge length, where do the
   cube's faces and edges land. CubeGlyph (and anything else that needs to
   draw the company's cube outside Scene's animation loop) is built on this.

   Same 30-degree grid Scene.tsx and BrandMark.tsx already use: EX/EY/EZ are
   the three edge directions, 'origin' is always the front-bottom corner
   (the lowest point on screen). */

export type Point = { x: number; y: number };

const EX: Point = { x: 0.866, y: -0.5 };
const EY: Point = { x: -0.866, y: -0.5 };
const EZ: Point = { x: 0, y: -1 };

function pt(origin: Point, a: number, b: number, c: number): Point {
  return {
    x: origin.x + a * EX.x + b * EY.x + c * EZ.x,
    y: origin.y + a * EX.y + b * EY.y + c * EZ.y,
  };
}

function fmt(n: number): string {
  return n.toFixed(1);
}

function pointsAttr(list: Point[]): string {
  return list.map((p) => `${fmt(p.x)},${fmt(p.y)}`).join(' ');
}

/* The eight named corners of one cube, front-bottom corner at 'origin',
   edge length 'altitude'. Same letters Scene.tsx uses: A is the origin
   itself, T is the top of the front vertical edge, TB is the far top
   corner (directly above A, twice the altitude up). */
function corners(origin: Point, altitude: number) {
  const A = { x: origin.x, y: origin.y }; // copy: never hand back the caller's own object
  const B = pt(origin, altitude, 0, 0);
  const C = pt(origin, 0, altitude, 0);
  const T = pt(origin, 0, 0, altitude);
  const TR = pt(origin, altitude, 0, altitude);
  const TL = pt(origin, 0, altitude, altitude);
  const TB = pt(origin, altitude, altitude, altitude);
  return { A, B, C, T, TR, TL, TB };
}

/* The three visible face outlines as SVG "points" strings, formatted
   exactly like Scene.tsx's own pts() helper (one decimal place) so a
   <polygon points={...}> built from this is byte-identical to Scene.tsx's. */
export function cubeFaces(origin: Point, altitude: number): { left: string; right: string; top: string } {
  const { A, B, C, T, TR, TL, TB } = corners(origin, altitude);
  return {
    left: pointsAttr([A, C, TL, T]),
    right: pointsAttr([A, B, TR, T]),
    top: pointsAttr([T, TR, TB, TL]),
  };
}

/* The 9 visible edges, in the same order Scene.tsx's DashedCube draws them:
   one rotation around the silhouette (A to B to TR to TB to TL to C to A),
   then the three spokes through the centre (A-T, T-TR, T-TL). Keeping this
   order is what keeps a crawl animation's flow coherent across every glyph
   that reuses it. */
export function cubeEdges(origin: Point, altitude: number): Array<[Point, Point]> {
  const { A, B, C, T, TR, TL, TB } = corners(origin, altitude);
  return [
    [A, B],
    [B, TR],
    [TR, TB],
    [TB, TL],
    [TL, C],
    [C, A],
    [A, T],
    [T, TR],
    [T, TL],
  ];
}

/* A viewBox sized to fit exactly one cube of this altitude plus a 1px
   margin on every side (room for a 1-2px stroke), and the origin to draw
   the cube at so every corner lands inside that margin.

   Derived from the corner spread in corners() above: relative to 'origin',
   x runs from -0.866*altitude (C and TL) to +0.866*altitude (B and TR), and
   y runs from -2*altitude (TB) to 0 (A). So a box (0.866*altitude + 1) wide
   on each side of the origin, sat 1px above its bottom edge, holds the
   whole cube with 1px of daylight all around. */
export function cubeViewBox(altitude: number): { width: number; height: number; origin: Point } {
  const halfWidth = 0.866 * altitude;
  const width = halfWidth * 2 + 2;
  const height = altitude * 2 + 2;
  return {
    width,
    height,
    origin: { x: halfWidth + 1, y: altitude * 2 + 1 },
  };
}
